/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2020. All rights reserved
------------------------------------------------------------------------------------------------- */
import { html, LitElement } from 'lit-element';
import '../../styles';
import '../../utils/device-identifier.js'; // just to pull in modrnzr
import { extendInteractiveArea } from '../../utils/interactive-area.js';
import { KEYCODE } from '../../utils/keycode.js';
import radioButtonStyles from './sh-radio-button.lit.scss.js';
/**
 * @prop {String} label - Default label. Defines the label of the radio button.
 * @prop {Boolean} active - Default false. Defines if a radio button is checked or not. Changes when clicked.
 * @prop {Boolean} neutral - Default false. If true, the active state will be gray instead of orange.
 * @prop {Boolean} disabled - Default false. Defines if a radio button is disabled. If true, it won't allow clicks.
 * @fires active-changed - Emitted when active state changes.
 * @fires clicked - DEPRECATED, use active-changed instead. Emitted when active state changes.
 */
export class SHRadioButton extends LitElement {
  static get properties() {
    return {
      label: { type: String, reflect: true },
      active: { type: Boolean, reflect: true },
      disabled: { type: Boolean, reflect: true },
      neutral: { type: Boolean, reflect: true },
      /** @ignore */
      role: { type: String, reflect: true },
    };
  }

  static get styles() {
    return [extendInteractiveArea(), radioButtonStyles];
  }

  render() {
    return html`
      <div class="radio-container">
        <input
          class="radioBtn"
          type="radio"
          name="${this.label ? this.label : ''}"
          ?checked="${this.active}"
          ?active="${this.active}"
          tabindex="-1"
        />
        <span class="pseudo-radio"></span>
        ${this.label ? html` <label>${this.label}</label>` : ''}
      </div>
    `;
  }
  constructor() {
    super();
    this.label = 'label';
  }

  attributeChangedCallback(name, oldval, newVal) {
    super.attributeChangedCallback(name, oldval, newVal);
    const val = newVal !== null;
    if (name === 'active') {
      this.setAttribute('aria-checked', val);
    }
  }

  connectedCallback() {
    super.connectedCallback();
    if (!Modernizr.hovermq) {
      this.classList.add('no-hovermq');
    }
    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'radio');
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('active')) {
      this.dispatchEvent(new Event(`active-changed`));
    }
    if (this.active) {
      this.setAttribute('tabindex', '0');
    } else {
      this.setAttribute('tabindex', '-1');
    }
  }

  firstUpdated() {
    this.addEventListener('click', function () {
      this.handleActive();
    });
    this.addEventListener('focus', function () {
      this.handleFocus();
    });
    this.addEventListener('blur', function () {
      this.handleBlur();
    });
  }

  handleFocus() {
    const pseudoRadio = this.shadowRoot.querySelector('.pseudo-radio');
    if (!this.disabled) {
      this.onkeyup = function (e) {
        if (e.keyCode === KEYCODE.TAB) {
          pseudoRadio.classList.add('focus');
        }
      };
      this.onkeydown = function (e) {
        let siblings = this.parentElement.querySelectorAll('SH-RADIO-BUTTON:not([disabled])');
        let target;
        if (e.keyCode === KEYCODE.ARROW_RIGHT || e.keyCode === KEYCODE.ARROW_DOWN) {
          //next not disabled sibling
          let posThis = Array.from(siblings).indexOf(this);
          if (posThis === siblings.length - 1) {
            target = siblings[0];
          } else {
            target = siblings[posThis + 1];
          }
          this.handleArrowInput(e, [KEYCODE.ARROW_RIGHT, KEYCODE.ARROW_DOWN], target);
        } else if (e.keyCode === KEYCODE.ARROW_LEFT || e.keyCode === KEYCODE.ARROW_UP) {
          //previous not disabled sibling
          let posThis = Array.from(siblings).indexOf(this);
          if (posThis === 0) {
            target = siblings[siblings.length - 1];
          } else {
            target = siblings[posThis - 1];
          }
          this.handleArrowInput(e, [KEYCODE.ARROW_LEFT, KEYCODE.ARROW_UP], target);
        }
      };
    }
  }

  handleArrowInput(e, triggerKeys, target) {
    if (triggerKeys.includes(e.keyCode) && target) {
      e.preventDefault();
      this.active = false;
      target.active = true;
      target.focus();
    }
  }

  handleBlur() {
    const pseudoRadio = this.shadowRoot.querySelector('.pseudo-radio');
    pseudoRadio.classList.remove('focus');
  }

  handleActive() {
    this.active = true;
    //TODO This has to be removed in future as we are already dispatching active-changed event
    this.dispatchEvent(new CustomEvent('clicked', { bubbles: true, detail: this }));
  }
}

customElements.define('sh-radio-button', SHRadioButton);
